import { buildCompactSupplierNeeds } from "./simulationSupplierNeeds.js";
import { formatMoney } from "@/lib/api/manufacturing";
import { SIMULATION_SIZES } from "@/lib/constants";
import { buildNeedsByProcess, buildNeedsBySupplier, formatDate } from "@/lib/manufacturingSimulationPrint";

let pdfMakePromise = null;

async function loadPdfMake() {
  if (!pdfMakePromise) {
    pdfMakePromise = (async () => {
      const [pdfMakeModule, pdfFontsModule] = await Promise.all([
        import("pdfmake/build/pdfmake"),
        import("pdfmake/build/vfs_fonts"),
      ]);
      const pdfMake = pdfMakeModule.default || pdfMakeModule;
      const fonts = pdfFontsModule.default || pdfFontsModule;
      const vfs = fonts?.pdfMake?.vfs || fonts?.vfs || fonts;
      if (vfs) {
        if (typeof pdfMake.addVirtualFileSystem === "function") {
          pdfMake.addVirtualFileSystem(vfs);
        } else {
          pdfMake.vfs = vfs;
        }
      }
      return pdfMake;
    })();
  }
  return pdfMakePromise;
}

async function loadImageAsDataUrl(url) {
  if (!url) return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

const COLORS = {
  ink: "#111111",
  muted: "#6b7280",
  line: "#bbbbbb",
  headerFill: "#f0f0f0",
  accent: "#1e3a8a",
};

const TABLE_LAYOUT = {
  hLineWidth: () => 0.5,
  vLineWidth: () => 0.5,
  hLineColor: () => COLORS.line,
  vLineColor: () => COLORS.line,
  paddingLeft: () => 3,
  paddingRight: () => 3,
  paddingTop: () => 2,
  paddingBottom: () => 2,
};

function headerCell(text, alignment = "center") {
  return { text, style: "th", alignment, fillColor: COLORS.headerFill };
}

function docHeader(model, subtitle, lineWidth = 515, showCosts = true) {
  return [
    { text: "Simulación de orden de fabricación", style: "title" },
    subtitle ? { text: subtitle, style: "subtitle", margin: [0, 0, 0, 4] } : null,
    {
      columns: [
        { stack: [{ text: "DESCRIPCIÓN", style: "lbl" }, { text: model.description || "—", style: "metaValue" }] },
        { stack: [{ text: "FECHA", style: "lbl" }, { text: formatDate(model.simulationDate) || "—", style: "metaValue" }] },
        { stack: [{ text: "ARTÍCULOS", style: "lbl" }, { text: String((model.items || []).length), style: "metaValue" }] },
        { stack: [{ text: "OBSERVACIONES", style: "lbl" }, { text: model.notes || "—", style: "metaValue" }] },
      ],
      columnGap: 12,
      margin: [0, 2, 0, 6],
    },
    {
      table: {
        widths: showCosts ? ["*", "*", "*", "*"] : ["*"],
        body: [[
          totalsBox("Total prendas", String(model.totals?.total_quantity ?? 0)),
          ...(showCosts ? [
          totalsBox("Total materiales", formatMoney(model.totals?.material_total)),
          totalsBox("Total procesos", formatMoney(model.totals?.process_total)),
          totalsBox("Coste total", formatMoney(model.totals?.grand_total), true),
          ] : []),
        ]],
      },
      layout: "noBorders",
      margin: [0, 0, 0, 10],
    },
    { canvas: [{ type: "line", x1: 0, y1: 0, x2: lineWidth, y2: 0, lineWidth: 1, lineColor: COLORS.ink }], margin: [0, 0, 0, 10] },
  ].filter(Boolean);
}

function totalsBox(label, value, accent = false) {
  return {
    table: {
      widths: ["*"],
      body: [[{
        stack: [
          { text: label.toUpperCase(), style: "lbl" },
          { text: value, bold: true, fontSize: 11, color: accent ? COLORS.accent : COLORS.ink },
        ],
      }]],
    },
    layout: {
      hLineWidth: () => 0.5,
      vLineWidth: () => 0.5,
      hLineColor: () => (accent ? COLORS.accent : COLORS.line),
      vLineColor: () => (accent ? COLORS.accent : COLORS.line),
      paddingLeft: () => 6,
      paddingRight: () => 6,
      paddingTop: () => 3,
      paddingBottom: () => 3,
    },
  };
}

function colorCell(line) {
  return {
    columns: [
      { width: 9, canvas: [{ type: "rect", x: 0, y: 1, w: 6, h: 6, r: 1, color: line.hex || "#d1d5db", lineWidth: 0.5, lineColor: "#999999" }] },
      { width: "*", text: line.description || "", margin: [2, 0, 0, 0] },
    ],
    columnGap: 0,
  };
}

function matrixTable(item) {
  const head = [
    headerCell("Color", "left"),
    headerCell("Contr. 1", "left"),
    headerCell("Contr. 2", "left"),
    headerCell("Contr. 3", "left"),
    ...SIMULATION_SIZES.map((size) => headerCell(size)),
    headerCell("Total"),
  ];
  const rows = (item.colorLines || []).map((line) => [
    colorCell(line),
    { text: line.contrast_1 || "", alignment: "left" },
    { text: line.contrast_2 || "", alignment: "left" },
    { text: line.contrast_3 || "", alignment: "left" },
    ...SIMULATION_SIZES.map((size) => ({ text: line.sizes[size] ? String(line.sizes[size]) : "", alignment: "center" })),
    { text: String(line.total), alignment: "center", bold: true },
  ]);
  const footer = [
    { text: "Total prendas", alignment: "left", bold: true, fillColor: COLORS.headerFill },
    { text: "", colSpan: 3, fillColor: COLORS.headerFill }, {}, {},
    ...SIMULATION_SIZES.map((size) => ({ text: String(item.sizeTotals[size] || 0), alignment: "center", bold: true, fillColor: COLORS.headerFill })),
    { text: String(item.totalQuantity), alignment: "center", bold: true, fillColor: COLORS.headerFill },
  ];
  return {
    style: "cell",
    table: {
      headerRows: 1,
      widths: ["*", "auto", "auto", "auto", ...SIMULATION_SIZES.map(() => "auto"), "auto"],
      body: [head, ...rows, footer],
    },
    layout: TABLE_LAYOUT,
    margin: [0, 0, 0, 6],
  };
}

const quantity = (value) => Number(value || 0).toLocaleString("es-ES", { maximumFractionDigits: 4 });
const materialDescription = (line) => [
  [line.material_code, line.name].filter(Boolean).join(" · "),
  `Proveedor: ${[line.supplier_code, line.supplier_name].filter(Boolean).join(" · ") || "Sin asignar"}`,
  `Ref.: ${line.supplier_ref || "Sin referencia"}`,
  line.supplier_description,
  line.material_season && `Temporada: ${line.material_season}`,
  line.process_name && `Proceso: ${line.process_name}`,
  line.notes,
].filter(Boolean).join("\n");

function materialsTable(item) {
  if (!item.materials.length) return { text: "El escandallo no contiene materiales.", style: "muted", margin: [0, 0, 0, 6] };
  const head = [headerCell("Material / proveedor / referencia", "left"), headerCell("Consumo / prenda"), headerCell("Merma"), headerCell("Necesidad total"), headerCell("Precio"), headerCell("Coste / prenda"), headerCell("Importe total")];
  const rows = item.materials.map((line) => [
    { text: materialDescription(line), alignment: "left" },
    { text: `${quantity(line.quantity_per_unit)} ${line.unit || ""}`, alignment: "right" },
    { text: `${quantity(line.waste_pct)}%`, alignment: "right" },
    { text: `${quantity(line.total_quantity)} ${line.unit || ""}`, alignment: "right" },
    { text: formatMoney(line.unit_cost), alignment: "right" },
    { text: formatMoney(line.unit_line_cost), alignment: "right" },
    { text: formatMoney(line.total_cost), alignment: "right" },
  ]);
  return { style: "cell", table: { headerRows: 1, widths: ["*", 43, 30, 48, 43, 43, 48], body: [head, ...rows] }, layout: TABLE_LAYOUT, margin: [0, 0, 0, 6] };
}

function processesTable(item) {
  if (!item.processes.length) return { text: "El escandallo no contiene procesos.", style: "muted", margin: [0, 0, 0, 6] };
  const head = [headerCell("Proceso / proveedor", "left"), headerCell("Cantidad / prenda"), headerCell("Precio"), headerCell("Coste / prenda"), headerCell("Importe total")];
  const rows = item.processes.map((line) => [
    { text: [line.name, [line.supplier_code, line.supplier_name].filter(Boolean).join(" · ") || "Sin proveedor", line.notes].filter(Boolean).join("\n"), alignment: "left" },
    { text: quantity(line.quantity_per_unit), alignment: "right" },
    { text: formatMoney(line.unit_cost), alignment: "right" },
    { text: formatMoney(line.unit_line_cost), alignment: "right" },
    { text: formatMoney(line.total_cost), alignment: "right" },
  ]);
  return { style: "cell", table: { headerRows: 1, widths: ["*", 60, 60, 60, 65], body: [head, ...rows] }, layout: TABLE_LAYOUT, margin: [0, 0, 0, 6] };
}

function cuttingTables(item) {
  return (item.cutting || []).flatMap((group) => [
    { text: `${group.name} · Consumos por color`, style: "h3" },
    { text: "Prendas × consumo en mtrs/prenda, sin merma. Los materiales vinculados no duplican costes.", style: "muted", margin: [0, 0, 0, 4] },
    ...group.materials.flatMap((line) => [
      { text: materialDescription(line), margin: [0, 4, 0, 4] },
      { style: "cell", table: { headerRows: 1, widths: ["*", 55, 85, 85], body: [
        [headerCell("Variante / color del material", "left"), headerCell("Prendas"), headerCell("Consumo mtrs/prenda"), headerCell("Necesidad mtrs")],
        ...line.colors.map((color) => [
          { text: [color.description, color.materialColor, [color.contrast_1 && `C1: ${color.contrast_1}`, color.contrast_2 && `C2: ${color.contrast_2}`, color.contrast_3 && `C3: ${color.contrast_3}`].filter(Boolean).join(" · ")].filter(Boolean).join("\n") },
          quantity(color.garments), quantity(line.consumptionMetres), quantity(color.totalMetres),
        ]),
        [{ text: "Total", bold: true }, quantity(item.totalQuantity), "", quantity(line.totalMetres)],
      ] }, layout: TABLE_LAYOUT },
    ]),
    ...(!group.materials.length ? [{ text: "Sin materiales de corte configurados.", style: "muted" }] : []),
  ]);
}

function articleSection(item, image, index) {
  const dataGrid = {
    columns: [
      { width: "*", stack: [labelValue("Temporada", item.season), labelValue("Ref. fabricante", item.manufacturer_reference)] },
      { width: "*", stack: [labelValue("Composición", item.composicion), labelValue("Código patrón", item.pattern_code)] },
      {
        width: "*",
        stack: [
          item.validated === null ? null : labelValue("Escandallo", item.validated ? "Validado" : "Sin validar"),
          labelValue("Observaciones", item.notes),
        ].filter(Boolean),
      },
    ],
    columnGap: 12,
  };

  const head = image
    ? { columns: [{ width: 150, image, fit: [150, 150] }, { width: "*", stack: [{ text: `${item.code} · ${item.description}`, style: "h2" }, dataGrid] }], columnGap: 12 }
    : { stack: [{ text: `${item.code} · ${item.description}`, style: "h2" }, dataGrid] };

  return {
    stack: [
      head,
      { text: "Cantidades por color y talla", style: "h3" },
      matrixTable(item),
      ...cuttingTables(item),
      { text: "Materiales necesarios (necesidad con merma)", style: "h3" },
      materialsTable(item),
      { text: "Procesos de fabricación", style: "h3" },
      processesTable(item),
      {
        columns: [
          totalsBox("Total prendas", String(item.totalQuantity)),
          totalsBox("Coste material/prenda", formatMoney(item.unitMaterialCost)),
          totalsBox("Coste proceso/prenda", formatMoney(item.unitProcessCost)),
          totalsBox("Coste/prenda", formatMoney(item.unitTotalCost)),
          totalsBox("Coste total artículo", formatMoney(item.totalCost), true),
        ],
        columnGap: 8,
        margin: [0, 4, 0, 0],
      },
    ],
    pageBreak: index === 0 ? undefined : "before",
  };
}

function labelValue(label, value) {
  return { stack: [{ text: label.toUpperCase(), style: "lbl" }, { text: value || "—", margin: [0, 0, 0, 4] }] };
}

export async function buildFullReportDocDefinition(model) {
  const images = await Promise.all((model.items || []).map((item) => loadImageAsDataUrl(item.image)));
  const content = [
    ...docHeader(model),
    ...(model.items || []).map((item, index) => articleSection(item, images[index], index)),
  ];
  return baseDocDefinition(content);
}

// One article starts on each page. Long articles may continue without dropping rows.
export async function buildCompactReportDocDefinition(model) {
  const images = await Promise.all((model.items || []).map((item) => loadImageAsDataUrl(item.image)));
  const content = (model.items || []).map((item, index) => {
    const groups = buildCompactSupplierNeeds(item);
    const body = [[headerCell("Color del material", "left"), headerCell("Artículo / uso", "left"), headerCell("Necesidad total", "right")]];
    for (const group of groups) {
      body.push([{ text: group.supplier, colSpan: 3, bold: true, fillColor: "#e8edf4", margin: [0, 2, 0, 2] }, {}, {}]);
      for (const reference of group.references) {
        body.push([{ text: `${reference.name} · Ref. ${reference.reference || "Sin referencia"} · ${reference.unit}`, colSpan: 3, bold: true, fillColor: "#f3f4f6" }, {}, {}]);
        for (const color of reference.colors) {
          body.push([
            { columns: [
              ...(color.hex ? [{ width: 12, canvas: [{ type: "ellipse", x: 4, y: 5, r1: 3.5, r2: 3.5, color: color.hex, lineColor: "#999999", lineWidth: 0.5 }] }] : []),
              { width: "*", text: color.color, bold: true, color: color.pending ? "#92400e" : COLORS.ink },
            ], columnGap: 0 },
            { text: color.usages.map(use => `${item.code || "Artículo"} · ${use.name}: ${quantity(use.totalQuantity)} ${reference.unit}`).join("\n") },
            { text: `${quantity(color.totalQuantity)} ${reference.unit}`, alignment: "right", bold: true },
          ]);
        }
      }
    }
    const heading = { stack: [
      { text: `${item.code} · ${item.description}`, style: "h2" },
      { text: [item.season && `Temporada: ${item.season}`, item.manufacturer_reference && `Ref. fabricante: ${item.manufacturer_reference}`, item.pattern_code && `Patrón: ${item.pattern_code}`, item.composicion].filter(Boolean).join(" · "), fontSize: 8 },
      ...(item.notes ? [{ text: item.notes, fontSize: 8, margin: [0, 3, 0, 0] }] : []),
    ] };
    return { pageBreak: index ? "before" : undefined, stack: [
      { text: `SIMULACIÓN · ${model.description || "Sin descripción"} · ${formatDate(model.simulationDate)}`, fontSize: 8, color: COLORS.muted, margin: [0, 0, 0, 5] },
      { columns: [...(images[index] ? [{ width: 65, image: images[index], fit: [65, 60] }] : []), { width: "*", ...heading }], columnGap: 10, margin: [0, 0, 0, 6], unbreakable: true },
      ...(model.notes ? [{ text: model.notes, fontSize: 8, margin: [0, 0, 0, 4] }] : []),
      matrixTable(item),
      { text: "Necesidades agrupadas por proveedor y color", style: "h3" },
      groups.length ? { style: "cell", table: { headerRows: 1, dontBreakRows: false, keepWithHeaderRows: 1, widths: [100, "*", 85], body }, layout: TABLE_LAYOUT } : { text: "Sin materiales ni procesos configurados.", fontSize: 8 },
      ...((item.processes || []).length ? [{ text: "Procesos: " + item.processes.map(process => `${process.name} (${process.supplier_name || "Sin proveedor"})`).join(" · "), fontSize: 8, margin: [0, 6, 0, 2] }] : []),
      { text: "Materiales: cantidades con la merma configurada. Color pendiente: revisar antes de comprar. Cada total mantiene su unidad.", fontSize: 7, color: COLORS.muted, margin: [0, 5, 0, 5] },
      { text: `Prendas: ${quantity(item.totalQuantity)}     Material/prenda: ${formatMoney(item.unitMaterialCost)}     Proceso/prenda: ${formatMoney(item.unitProcessCost)}     Coste/prenda: ${formatMoney(item.unitTotalCost)}     Total: ${formatMoney(item.totalCost)}`, fontSize: 8, bold: true },
    ] };
  });
  const definition = baseDocDefinition(content);
  definition.pageMargins = [28, 28, 28, 30];
  definition.footer = (currentPage, pageCount) => ({ text: `${currentPage} / ${pageCount}`, alignment: "right", fontSize: 7, color: COLORS.muted, margin: [28, 8, 28, 0] });
  return definition;
}

export function buildNeedsByProcessDocDefinition(model) {
  const { groups, grandTotalCost } = buildNeedsByProcess(model);
  const unitPrice = (value) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(Number(value || 0));
  const numeric = (value, bold = false) => ({ text: value, alignment: "right", bold });
  const sections = groups.flatMap((group) => {
    const processLabel = [group.process_code, group.process].filter(Boolean).join(" · ");
    const summaryHead = ["Material / descripción", "Proveedor / referencia", "Base", "Merma", "A comprar", "Unidad", "Precio / unidad", "Importe"].map((label) => headerCell(label, "left"));
    const rows = group.materials.map((line) => [
      { text: [[line.material_code, line.name].filter(Boolean).join(" · "), line.supplier_description, line.material_season && `Temporada: ${line.material_season}`, ...line.notes,
        line.colorNeeds.length && `Por color del artículo: ${line.colorNeeds.map((color) => {
          const contrasts = [color.contrast_1 && `C1: ${color.contrast_1}`, color.contrast_2 && `C2: ${color.contrast_2}`, color.contrast_3 && `C3: ${color.contrast_3}`].filter(Boolean).join(", ");
          return `${color.description}${contrasts ? ` (${contrasts})` : ""}: ${quantity(color.totalQuantity)} ${line.unit}`;
        }).join("; ")}`].filter(Boolean).join("\n") },
      { text: [[line.supplier_code, line.supplier_name].filter(Boolean).join(" · ") || "Sin proveedor", `Ref.: ${line.supplier_ref || "Sin referencia"}`].join("\n") },
      numeric(quantity(line.totalBaseQuantity)), numeric(quantity(line.totalWasteQuantity)), numeric(quantity(line.totalQuantity), true),
      { text: line.unit || "Sin unidad" },
      numeric(line.prices.length === 1 ? unitPrice(line.prices[0]) : `${unitPrice(line.prices[0])} - ${unitPrice(line.prices.at(-1))}`),
      numeric(formatMoney(line.totalCost), true),
    ]);
    const details = group.materials.map((line) => {
      const identity = [line.material_code, line.name, line.supplier_name || "Sin proveedor", `Ref. ${line.supplier_ref || "sin referencia"}`].filter(Boolean).join(" · ");
      const head = ["Artículo / observaciones", "Color del artículo / necesidad con merma", "Prendas", "Consumo / prenda", "Merma %", "Base", "A comprar", "Precio / unidad", "Importe"].map((label) => headerCell(label, "left"));
      const detailRows = line.contributions.map((row) => [
        { text: [[row.code, row.description].filter(Boolean).join(" · "), row.season && `Temporada: ${row.season}`, row.notes].filter(Boolean).join("\n") },
        { text: row.colors.length ? row.colors.map((color) => [
          `${color.description}: ${quantity(color.garments)} prendas / ${quantity(color.totalQuantity)} ${line.unit}`,
          [color.contrast_1 && `C1: ${color.contrast_1}`, color.contrast_2 && `C2: ${color.contrast_2}`, color.contrast_3 && `C3: ${color.contrast_3}`].filter(Boolean).join(" · "),
        ].filter(Boolean).join("\n")).join("\n") : "Sin desglose de color" },
        numeric(quantity(row.garments)), numeric(`${quantity(row.consumption)} ${line.unit}`), numeric(quantity(row.wastePct)),
        numeric(quantity(row.baseQuantity)), numeric(`${quantity(row.totalQuantity)} ${line.unit}`, true),
        numeric(unitPrice(row.unitPrice)), numeric(formatMoney(row.totalCost)),
      ]);
      return {
        style: "cell",
        table: { headerRows: 2, keepWithHeaderRows: 1, dontBreakRows: true, widths: [140, "*", 40, 60, 40, 50, 65, 50, 65], body: [
          [{ text: `Desglose por artículo · ${processLabel} | ${identity} | ${line.articleCount} artículo(s)`, colSpan: 9, bold: true, fillColor: "#eaf0f8" }, {}, {}, {}, {}, {}, {}, {}, {}],
          head, ...detailRows,
          [{ text: "Total material", colSpan: 5, bold: true, fillColor: COLORS.headerFill }, {}, {}, {}, {}, numeric(quantity(line.totalBaseQuantity), true), numeric(`${quantity(line.totalQuantity)} ${line.unit}`, true), { text: "" }, numeric(formatMoney(line.totalCost), true)],
        ] }, layout: TABLE_LAYOUT, margin: [0, 0, 0, 12],
      };
    });
    return [
      { text: group.process === "Sin proceso" ? "Materiales sin proceso asignado" : `Proceso: ${processLabel}`, style: "h2", margin: [0, 8, 0, 6] },
      { style: "cell", table: { headerRows: 2, keepWithHeaderRows: 1, dontBreakRows: true, widths: ["*", 145, 48, 48, 58, 32, 58, 64], body: [
        [{ text: `Compra de materiales · ${processLabel}`, colSpan: 8, bold: true, fillColor: "#eaf0f8" }, {}, {}, {}, {}, {}, {}, {}],
        summaryHead, ...rows,
        [{ text: "Subtotal materiales del proceso", colSpan: 7, bold: true, fillColor: COLORS.headerFill }, {}, {}, {}, {}, {}, {}, numeric(formatMoney(group.processTotalCost), true)],
      ] }, layout: TABLE_LAYOUT, margin: [0, 0, 0, 8] },
      ...details,
    ];
  });
  const content = [
    ...docHeader(model, "Necesidades de compra por procesos · Todos los artículos", 762),
    { text: "Base = prendas × consumo. A comprar = base + merma del escandallo, sin descontar existencias. Los importes corresponden solo a materiales; el servicio de los procesos no se incluye en el total de compra. Corte y tejidos se expresan en mtrs.", style: "muted", margin: [0, 0, 0, 8] },
    ...sections,
    ...(!groups.length ? [{ text: "La simulación no contiene materiales.", style: "muted" }] : []),
    { columns: [{ width: "*", text: "" }, totalsBox("Total compra de materiales", formatMoney(grandTotalCost), true)], margin: [0, 4, 0, 0] },
  ];
  return {
    ...baseDocDefinition(content), pageOrientation: "landscape",
    footer: (page, count) => ({ text: `Necesidades por procesos · Página ${page} de ${count}`, alignment: "right", margin: [40, 12, 40, 0], fontSize: 8, color: COLORS.muted }),
  };
}

function buildNeedsBySupplierDocDefinition(model) {
  const { groups } = buildNeedsBySupplier(model);
  const sections = groups.length
    ? groups.map((group) => {
        const head = [
          headerCell("Concepto", "left"),
          headerCell("Ref. material", "left"),
          headerCell("Cantidad total"),
        ];
        const rows = group.lines.flatMap((line) => [[
          { text: line.name, alignment: "left" },
          { text: line.reference || "—", alignment: "left" },
          { text: String(line.totalQuantity), alignment: "right" },
        ], ...line.colorNeeds.map((color) => [
          { text: `Color: ${color.color}${[1, 2, 3].map((index) => color[`contrast_${index}`] ? ` · C${index}: ${color[`contrast_${index}`]}` : "").join("")}`, margin: [10, 0, 0, 0], alignment: "left" },
          { text: line.reference || "—", alignment: "left" },
          { text: String(color.totalQuantity), alignment: "right" },
        ])]);
        return {
          stack: [
            { text: group.supplier, style: "h3" },
            {
              style: "cell",
              table: { headerRows: 1, widths: ["*", "auto", "auto"], body: [head, ...rows] },
              layout: TABLE_LAYOUT,
              margin: [0, 0, 0, 10],
            },
          ],
          unbreakable: false,
        };
      })
    : [{ text: "La simulación no contiene materiales ni procesos con proveedor.", style: "muted" }];

  const content = [
    ...docHeader(model, "Cálculo de necesidades por proveedor", 515, false),
    ...sections,
  ];
  return baseDocDefinition(content);
}

function baseDocDefinition(content) {
  return {
    pageSize: "A4",
    pageMargins: [40, 40, 40, 40],
    defaultStyle: { fontSize: 8, color: COLORS.ink },
    content,
    styles: {
      title: { fontSize: 16, bold: true, margin: [0, 0, 0, 2] },
      subtitle: { fontSize: 11, bold: true, color: COLORS.accent },
      h2: { fontSize: 12, bold: true, margin: [0, 0, 0, 4] },
      h3: { fontSize: 10, bold: true, margin: [0, 8, 0, 4] },
      th: { fontSize: 8, bold: true },
      cell: { fontSize: 8 },
      lbl: { fontSize: 7, color: COLORS.muted, characterSpacing: 0.3 },
      metaValue: { fontSize: 9, bold: true },
      muted: { fontSize: 9, italics: true, color: COLORS.muted },
    },
  };
}

function buildFileName(model, reportType) {
  const base = (model.description || "simulacion").trim().replace(/[^\w\d-]+/g, "_").replace(/_+/g, "_").slice(0, 60) || "simulacion";
  const date = model.simulationDate || "";
  const suffix = reportType === "compact" ? "compacto" : reportType === "needs_by_process"
    ? "necesidades_procesos"
    : reportType === "needs_by_supplier"
      ? "necesidades_proveedores"
      : "completo";
  return `${base}_${suffix}${date ? `_${date}` : ""}.pdf`;
}

export const SIMULATION_REPORT_TYPES = {
  FULL: "full",
  COMPACT: "compact",
  NEEDS_BY_PROCESS: "needs_by_process",
  NEEDS_BY_SUPPLIER: "needs_by_supplier",
};

function buildDocDefinition(model, reportType) {
  if (reportType === SIMULATION_REPORT_TYPES.NEEDS_BY_SUPPLIER) {
    return buildNeedsBySupplierDocDefinition(model);
  }
  return buildNeedsByProcessDocDefinition(model);
}

export async function generateSimulationPdf(model, { reportType = SIMULATION_REPORT_TYPES.FULL } = {}) {
  if (!model?.items?.length) return { ok: false, error: "empty" };

  const previewWindow = window.open("", "_blank");
  if (previewWindow) {
    previewWindow.document.title = "Generando PDF...";
    previewWindow.document.body.innerHTML = "<p style=\"font-family:sans-serif;padding:24px\">Generando PDF...</p>";
  }

  const pdfMake = await loadPdfMake();
  const docDefinition = reportType === SIMULATION_REPORT_TYPES.FULL
    ? await buildFullReportDocDefinition(model)
    : reportType === SIMULATION_REPORT_TYPES.COMPACT
      ? await buildCompactReportDocDefinition(model)
      : buildDocDefinition(model, reportType);
  const fileName = buildFileName(model, reportType);
  const pdf = pdfMake.createPdf(docDefinition);

  try {
    if (previewWindow) {
      await pdf.open(previewWindow);
    } else {
      await pdf.download(fileName);
    }
    return { ok: true, fileName };
  } catch (error) {
    previewWindow?.close?.();
    await pdf.download(fileName);
    return { ok: true, fileName, fallback: "download" };
  }
}
